import { useAuth } from "../providers/AuthContext";
import { useEffect, useRef, useState } from "react";

const baseUrl = process.env.REACT_APP_GITO_NOTIFY_URL + "/notify";
//const baseUrl = "https://localhost:7188" + "/notify";

export function useNotify(events = [], onMessage) {
    const { getAccessToken, isAuthenticated } = useAuth();
    const [connected, setConnected] = useState(false);
    const eventSourceRef = useRef(null);
    const reconnectTimer = useRef(null);
    const startingRef = useRef(false);
    const isMountedRef = useRef(false);

    const cleanup = () => {
        if (eventSourceRef.current) {
            console.log("💥 Closing SSE connection");
            eventSourceRef.current.close();
            eventSourceRef.current = null;
        }
        clearTimeout(reconnectTimer.current);
        startingRef.current = false;
        setConnected(false);
    };

    const scheduleReconnect = (delay = 3000) => {
        if (!isMountedRef.current || events.length === 0) {
            console.log("🛑 Skipping reconnect (no events)");
            return;
        }
        if (startingRef.current) return;
        console.log(`🔄 Reconnecting SSE in ${delay}ms...`);
        clearTimeout(reconnectTimer.current);
        reconnectTimer.current = setTimeout(() => startSSE(), delay);
    };

    const startSSE = async () => {

        if (eventSourceRef.current) {
            console.log("⚠️ SSE already active — skipping");
            return;
        }

        if (!isMountedRef.current) {
            console.log("🚫 Not mounted — skipping start");
            return;
        }

        if (!isAuthenticated) {
            console.log("⚠️ Not authenticated, skipping SSE");
            return;
        }

        // 🔥 Prevent double SSE init (React 18 StrictMode)
        if (eventSourceRef.current) {
            console.log("⚠️ SSE already active — skipping");
            return;
        }

        if (startingRef.current) return;

        if (!events || events.length === 0) {
            console.log("⚠️ No events to subscribe to, skipping SSE");
            return;
        }

        startingRef.current = true;

        try {
            const accessToken = await getAccessToken();
            if (!accessToken) {
                console.warn("❌ No access token available for SSE");
                startingRef.current = false;
                scheduleReconnect(5000);
                return;
            }

            const sseUrl = `${baseUrl}/auth/sse-token`;
            const res = await fetch(sseUrl, {
                headers: { Authorization: `Bearer ${accessToken}` },
            });

            if (!res.ok) {
                console.error("❌ Failed to get SSE token:", res.status);
                startingRef.current = false;
                scheduleReconnect(5000);
                return;
            }

            const { token: sseToken } = await res.json();

            const url = `${baseUrl}/updates/stream?events=${encodeURIComponent(
                events.join(",")
            )}&token=${sseToken}`;

            console.log("🚀 Starting SSE:", url);
            const es = new EventSource(url);
            eventSourceRef.current = es;

            es.onopen = () => {
                console.log("✅ SSE connected");
                if (isMountedRef.current) setConnected(true);
                startingRef.current = false;
            };

            es.onerror = (err) => {
                console.warn("⚠️ SSE error:", err);
                cleanup();
                scheduleReconnect(3000);
            };

            es.addEventListener("tokenExpired", () => {
                console.log("⏰ SSE token expired – reconnecting");
                cleanup();
                scheduleReconnect(1000);
            });

            // Default message handler
            es.onmessage = (e) => {
                try {
                    const data = JSON.parse(e.data);
                    onMessage?.({ event: "status", data });
                } catch {
                    onMessage?.({ event: "status", data: e.data });
                }
            };

            // Event listeners for each event
            events.forEach((evt) => {
                es.addEventListener(evt, (e) => {
                    try {
                        const data = JSON.parse(e.data);
                        onMessage?.({ event: evt, data });
                    } catch {
                        onMessage?.({ event: evt, data: e.data });
                    }
                });
            });
        } catch (err) {
            console.error("💣 SSE start error:", err);
            cleanup();
            scheduleReconnect(5000);
        }
    };

    useEffect(() => {
        isMountedRef.current = true;

        if (isAuthenticated && events.length > 0) {
            console.log("🏁 Starting SSE (events found)");
            startSSE();
        } else {
            console.log("⏸ No events or not authenticated — cleaning up SSE");
            cleanup();
        }

        return () => {
            console.log("🚪 Component unmounted — cleaning up SSE");
            isMountedRef.current = false;
            cleanup();
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isAuthenticated, events.join("|")]);

    return { connected };
}
